import type { AgentRuntimeOverride } from '@/lib/app-settings';
import { agentCommandName } from '@/lib/desktop-shell/cloud-agents';
import type { NativeAgent } from '@/lib/native-desktop';

/** Human-facing command name, hiding realpath details from version managers and npm. */
export function detectedAgentCommandName(agent: NativeAgent): string {
  const name = agentCommandName(agent.command);
  if (name === 'npx' || name === 'npx.cmd' || name.startsWith('npx-cli.')) return 'npx';
  // Some macOS packages (notably Homebrew's OpenCode formula) install a
  // native Mach-O binary whose internal filename ends in `.exe`. Keep that
  // packaging detail out of the command users see and edit.
  if (name.endsWith('.exe')) return name.slice(0, -'.exe'.length);
  return name;
}

function quoteLaunchToken(value: string): string {
  if (/^[a-zA-Z0-9_./:@%+=,-]+$/.test(value)) return value;
  return `'${value.replaceAll("'", `'"'"'`)}'`;
}

export function formatAgentLaunchCommand(command: string, args: readonly string[]): string {
  return [command, ...args].map(quoteLaunchToken).join(' ');
}

/** Parse a command line without invoking a shell or expanding variables. */
export function parseAgentLaunchCommand(value: string): { command: string; args: string[] } | null {
  const tokens: string[] = [];
  let token = '';
  let quote: 'single' | 'double' | null = null;
  let escaped = false;
  let started = false;
  for (const character of value.trim()) {
    if (escaped) {
      token += character;
      escaped = false;
      started = true;
    } else if (character === '\\' && quote !== 'single') {
      escaped = true;
      started = true;
    } else if (character === "'" && quote !== 'double') {
      quote = quote === 'single' ? null : 'single';
      started = true;
    } else if (character === '"' && quote !== 'single') {
      quote = quote === 'double' ? null : 'double';
      started = true;
    } else if (/\s/.test(character) && quote === null) {
      if (started) tokens.push(token);
      token = '';
      started = false;
    } else {
      token += character;
      started = true;
    }
  }
  if (escaped) token += '\\';
  if (started) tokens.push(token);
  const [command, ...args] = tokens;
  return command ? { command, args } : null;
}

/** Human-facing launch command including the arguments Crowdex discovered. */
export function detectedAgentLaunchCommand(agent: NativeAgent): string {
  return formatAgentLaunchCommand(detectedAgentCommandName(agent), agent.args);
}

/** Apply user-owned local launch settings to the agents discovered on this machine. */
export function applyAgentRuntimeOverrides(
  agents: readonly NativeAgent[],
  overrides: Readonly<Record<string, AgentRuntimeOverride>>,
): NativeAgent[] {
  return agents.map((agent) => {
    const override = overrides[agent.id];
    if (override === undefined) return agent;
    const launch =
      override.launchCommand === undefined ? null : parseAgentLaunchCommand(override.launchCommand);
    return {
      ...agent,
      command: launch?.command ?? (override.command?.trim() || agent.command),
      args: launch?.args ?? override.args ?? agent.args,
      env: agent.env,
    };
  });
}
